import { subMonths } from 'date-fns'

/**
 * Date utilities for Admin Frontend
 * 
 * Handles date formatting and timezone conversion for API communication.
 * All dates sent to backend should be in ISO 8601 format with timezone offset.
 * Backend expects dates in UTC, so we convert from client timezone to UTC.
 */

/**
 * Get client timezone offset in minutes
 * @returns Timezone offset in minutes (e.g., -420 for UTC-7, 420 for UTC+7)
 */
export function getClientTimezoneOffset(): number {
  return new Date().getTimezoneOffset() * -1; // Invert because getTimezoneOffset returns opposite
}

/**
 * Get client timezone as IANA timezone string (e.g., "Asia/Ho_Chi_Minh")
 * Falls back to offset-based timezone if IANA not available
 */
export function getClientTimezone(): string {
  try {
    // Try to get IANA timezone (works in modern browsers)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      return timezone;
    }
  } catch {
    // Fallback to offset-based
  }
  
  // Fallback: return offset-based timezone
  const offset = getClientTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Convert a date to ISO 8601 string with timezone offset for API
 * This ensures backend receives date with timezone context
 * 
 * @param date - Date object or ISO string
 * @param includeTime - Whether to include time (default: false for date-only)
 * @returns ISO 8601 string with timezone (e.g., "2025-11-15T00:00:00+07:00")
 * 
 * @example
 * formatDateForAPI(new Date('2025-11-15')) // "2025-11-15T00:00:00+07:00"
 * formatDateForAPI('2025-11-15') // "2025-11-15T00:00:00+07:00"
 */
export function formatDateForAPI(date: Date | string | undefined, includeTime: boolean = false): string | undefined {
  if (!date) return undefined;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return undefined;
  }

  // For date-only, set to start of day in local timezone
  if (!includeTime) {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    // Format as ISO with timezone offset
    const offset = getClientTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offset) / 60);
    const offsetMinutes = Math.abs(offset) % 60;
    const offsetSign = offset >= 0 ? '+' : '-';
    const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
    
    return `${year}-${month}-${day}T00:00:00${offsetStr}`;
  }

  // For datetime, use ISO string with timezone
  return dateObj.toISOString().replace('Z', getTimezoneOffsetString());
}

/**
 * Get timezone offset string (e.g., "+07:00", "-05:00")
 */
function getTimezoneOffsetString(): string {
  const offset = getClientTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Convert date range to API format
 * 
 * @param fromDate - Start date
 * @param toDate - End date
 * @param includeTime - Whether to include time
 * @returns Object with fromDate and toDate in API format
 */
export function formatDateRangeForAPI(
  fromDate: Date | string | undefined,
  toDate: Date | string | undefined,
  includeTime: boolean = false
): { fromDate?: string; toDate?: string } {
  return {
    fromDate: formatDateForAPI(fromDate, includeTime),
    toDate: formatDateForAPI(toDate, includeTime),
  };
}

/**
 * How far back an admin list looks when the operator has not picked a range.
 *
 * Every admin table (resources, users, tenants, payments) opens on a bounded window rather than the
 * whole table: the default view is "what happened recently", and none of the list endpoints has an
 * index that makes an unbounded cross-tenant scan cheap once these tables grow (see
 * docs/analysis/admin-list-index-coverage.md). Six months is long enough to cover a billing cycle
 * and the support questions that follow it.
 */
export const DEFAULT_LIST_RANGE_MONTHS = 6

/**
 * The default created-at window for an admin list: the last `months` months up to today.
 *
 * Both ends are whole local calendar days serialized the way DateRangePicker serializes its own
 * selection (`Date.toISOString()`), so the value round-trips through the picker and through the
 * filter cache unchanged, and `formatDateForAPI` can read the operator's calendar day back off it.
 *
 * `now` is a parameter so this is pure and testable; production callers pass nothing.
 *
 * @example lastMonthsRange(6, new Date('2026-08-24T10:00:00+07:00')) // 2026-02-24 → 2026-08-24
 */
export function lastMonthsRange(
  months: number = DEFAULT_LIST_RANGE_MONTHS,
  now: Date = new Date()
): { from: string; to: string } {
  // Local midnight, matching the calendar's own day-granular selection: the pages format these
  // with includeTime=false, so any time-of-day would be dropped on the way out anyway, and keeping
  // it would make the picker render a time it never offered.
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  // subMonths rather than setMonth(-months): setMonth overflows the short months, so on 31/08 it
  // would land on 03/03 instead of 28/02.
  const from = subMonths(to, months)

  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * Parse date from API response (assumes UTC from backend)
 * Converts UTC date to local date for display
 * 
 * @param dateString - ISO string from API (UTC)
 * @returns Date object in local timezone
 */
export function parseDateFromAPI(dateString: string | undefined): Date | undefined {
  if (!dateString) return undefined;
  return new Date(dateString);
}

/**
 * Format date for display (local timezone)
 * 
 * @param date - Date object or ISO string
 * @param format - Format string (default: 'YYYY-MM-DD')
 * @returns Formatted date string
 */
export function formatDateForDisplay(date: Date | string | undefined, format: string = 'YYYY-MM-DD'): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  if (format === 'YYYY-MM-DD') {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Default: use Intl.DateTimeFormat
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(dateObj);
}

/**
 * A UTC instant as `dd-MM-yyyy HH:mm GMT+7`, the admin panel's datetime format pinned to Vietnam
 * time rather than the browser's zone. A string with no zone designator is read as UTC — the
 * backend serialises some `DateTime`s without the trailing `Z`.
 */
export function formatVietnamDateTime(utcIso: string): string {
  const instant = parseUtcIso(utcIso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Ho_Chi_Minh",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  return `${part("day")}-${part("month")}-${part("year")} ${part("hour")}:${part("minute")} GMT+7`;
}

/**
 * Reads a UTC timestamp from the API as the instant it names. The API may send one without a zone
 * suffix, which `new Date` would take as local time — hours off for an operator on +07:00.
 */
export function parseUtcIso(utcIso: string): Date {
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(utcIso);
  return new Date(hasZone ? utcIso : `${utcIso}Z`);
}
